import { Controller } from "@hotwired/stimulus";

// Weekly brand visibility in the answers citing outreach URLs, with a marker
// on each outreach action date. A "volume" series is drawn as bars on a right
// axis (answers citing the URLs). Connects to data-controller="outreach-chart".
export default class extends Controller {
  static values = { labels: Array, series: Array, markers: Array };

  connect() {
    if (!window.ApexCharts) return;

    this.chart = new window.ApexCharts(this.element, this.options());
    this.chart.render().then(() => {
      this.seriesValue.filter((serie) => serie.hidden).forEach((serie) => this.chart.hideSeries(serie.name));
    });
  }

  disconnect() {
    this.chart?.destroy();
  }

  isVolume(serie) {
    return serie.axis === "volume";
  }

  yaxes() {
    const rateSeriesName = this.seriesValue.find((serie) => !this.isVolume(serie))?.name;
    let rateAxisShown = false;

    return this.seriesValue.map((serie) => {
      if (this.isVolume(serie)) {
        return {
          seriesName: serie.name,
          opposite: true,
          min: 0,
          forceNiceScale: true,
          title: { text: "Answers" },
          labels: { formatter: (value) => (value == null ? "" : Math.round(value)) },
        };
      }

      const axis = {
        seriesName: rateSeriesName,
        show: !rateAxisShown,
        min: 0,
        labels: { formatter: (value) => (value == null ? "" : `${Math.round(value * 10) / 10}%`) },
      };
      rateAxisShown = true;
      return axis;
    });
  }

  options() {
    const styles = getComputedStyle(document.documentElement);
    const textColor = styles.getPropertyValue("--color-base-content").trim() || "#64748b";
    const series = this.seriesValue;

    return {
      chart: { type: "line", height: 340, toolbar: { show: false }, zoom: { enabled: false }, foreColor: textColor },
      series: series.map((serie) => ({ name: serie.name, type: serie.type || "line", data: serie.data })),
      colors: series.map((serie) => serie.color),
      stroke: {
        width: series.map((serie) => (this.isVolume(serie) ? 0 : serie.dashed ? 2 : 3)),
        dashArray: series.map((serie) => (serie.dashed ? 6 : 0)),
        curve: "straight",
      },
      fill: { opacity: series.map((serie) => (this.isVolume(serie) ? 0.35 : 1)) },
      plotOptions: { bar: { columnWidth: "60%" } },
      markers: { size: series.map((serie) => (this.isVolume(serie) ? 0 : 3)) },
      xaxis: { categories: this.labelsValue, tickAmount: Math.min(this.labelsValue.length, 12) },
      yaxis: this.yaxes(),
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value, { seriesIndex, dataPointIndex }) => {
            const serie = series[seriesIndex];
            if (this.isVolume(serie)) return `${value ?? 0} answers`;
            if (value == null) return "no answer citing it";
            const counts = serie.counts?.[dataPointIndex];
            return counts ? `${value}% (${counts})` : `${value}%`;
          },
        },
      },
      annotations: {
        // Orange, on top: number of brand mentions put online that week.
        // Purple, at the bottom: prompts that start (+) or stop (−) being tracked.
        xaxis: this.markersValue.map((marker) => {
          const prompts = marker.kind === "prompts";
          const color = prompts ? "#a78bfa" : "#f59e0b";
          return {
            x: marker.label,
            borderColor: prompts ? "rgba(167, 139, 250, 0.45)" : "rgba(245, 158, 11, 0.45)",
            strokeDashArray: 3,
            label: {
              text: marker.text,
              orientation: "horizontal",
              position: prompts ? "bottom" : "top",
              borderColor: color,
              offsetY: prompts ? 0 : -4,
              style: { background: color, color: "#111827", fontSize: "10px", fontWeight: 600, padding: { left: 4, right: 4, top: 1, bottom: 1 } },
            },
          };
        }),
      },
      legend: { position: "top" },
      grid: { borderColor: "rgba(148, 163, 184, 0.2)" },
    };
  }
}
